import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { InternalLinks } from "@/components/InternalLinks";
import { MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const NearbyAreas = () => {
  const { ref, isVisible } = useScrollAnimation();
  const areas = [
    { zone: "Vallejo", cities: "Centro de Vallejo, Hiddenbrooke, Glen Cove, Springs Road" },
    { zone: "Benicia", cities: "Benicia, American Canyon, Mare Island" },
    { zone: "Fairfield", cities: "Fairfield, Suisun City, Green Valley" },
    { zone: "Vacaville", cities: "Vacaville, Dixon, Elmira" },
  ];
  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-10 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Vecindarios que Servimos en Vallejo</h2>
          <p className="text-muted-foreground">Limpieza profesional a domicilio en Vallejo, Benicia, American Canyon y ciudades del Condado de Solano.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {areas.map((area, i) => (
            <div key={i} className={cn("bg-background rounded-2xl p-6 border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 text-center anim-fade-up", isVisible && "is-visible")} style={{ transitionDelay: `${i * 100}ms` }}>
              <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
              <h3 className="font-bold text-primary mb-2">{area.zone}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{area.cities}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center flex flex-col sm:flex-row justify-center gap-4">
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold px-10 py-6 h-auto rounded-2xl shadow-lg">
            <a href={BOOKING_URL}>Reservar Ahora en Vallejo</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 hover:text-primary font-bold px-10 py-6 h-auto rounded-2xl">
            <a href={BOOKING_URL}>Ver Mi Precio al Instante</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

const VallejoSpanishPage = () => (
  <LocationContext.Provider value={{ slug: "vallejo", name: "Vallejo", type: "city", regionSlug: "solano" }}>
    <SEO
      title="Limpieza del Hogar en Vallejo, CA — Uplevel Cleaning"
      description="Servicio profesional de limpieza del hogar en Vallejo, CA. Precios planos, reserva en 60 segundos, garantía del 200%. Limpieza estándar, profunda y de mudanza."
      keywords="limpieza vallejo, maid service vallejo español, servicio de limpieza vallejo ca, empleada doméstica vallejo, limpieza condado solano"
      url="https://uplevelcleaning.com/es/vallejo"
      locationName="Vallejo"
      regionSlug="solano"
      lang="es"
      includeLocalBusiness={true}
      includeFAQ={true}
      breadcrumbs={[{ name: "Inicio", url: "/" }, { name: "Condado de Solano", url: "/es/solano" }, { name: "Vallejo", url: "/es/vallejo" }]}
      reviews={[
        { author: "Rosa M.", rating: 5, reviewBody: "En Vallejo es difícil encontrar un servicio de limpieza confiable. Uplevel llegó a tiempo, limpió a fondo y el precio fue exactamente lo que cotizaron. ¡Fantástico!", datePublished: "2026-03-08" },
        { author: "Héctor G.", rating: 5, reviewBody: "Contraté una limpieza de mudanza para mi apartamento en Vallejo. El resultado fue perfecto. Me regresaron el depósito completo. ¡Muy agradecido!", datePublished: "2026-02-01" },
      ]}
    />
    <div className="flex min-h-screen flex-col" lang="es">
      <StickyBanner />
      <Navbar />
      <main className="flex-1">
        <SpanishHero cityName="Vallejo" regionSlug="solano" />
        <div className="bg-primary/5 border-b border-primary/10 py-4">
          <div className="container flex flex-wrap justify-center gap-6 text-sm font-medium text-foreground">
            {["Servicio Local en Vallejo", "Condado de Solano", "Garantía del 200%", "Reserva en 60 Segundos"].map((item, i) => (
              <span key={i} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> {item}</span>
            ))}
          </div>
        </div>
        <SpanishWhyUs cityName="Vallejo" />
        <NearbyAreas />
        <SpanishServices />

        <FrequencyDiscounts />
        <PricingCalculator />
        <SpanishTestimonials region="solano" />
        <Guarantee />
        <SpanishFAQ cityName="Vallejo" />
        <InternalLinks />
        <SpanishFinalCTA cityName="Vallejo" />
      </main>
      <Footer />
    </div>
  </LocationContext.Provider>
);

export default VallejoSpanishPage;
